import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Send, CheckCircle } from 'lucide-react';
import './AuditSurvey.css';

const FORMSPREE_ID = 'xrewjowz';

const SECTIONS = [
  {
    id: 'coverage',
    title: 'Test Coverage',
    max: 24,
    items: [
      { id: 'c1',  text: 'Critical happy-path user flows have automated tests', priority: 'high' },
      { id: 'c2',  text: 'Login / authentication flow is tested', priority: 'high' },
      { id: 'c3',  text: 'Core data entry / form submission flows are tested', priority: 'high' },
      { id: 'c4',  text: 'Payment or checkout flow is tested (if applicable)', priority: 'high' },
      { id: 'c5',  text: 'API endpoints have automated tests', priority: 'med' },
      { id: 'c6',  text: 'Unit tests exist for core business logic', priority: 'med' },
      { id: 'c7',  text: 'Error states and edge cases have tests', priority: 'med' },
      { id: 'c8',  text: 'Mobile / responsive layouts are tested', priority: 'med' },
      { id: 'c9',  text: 'Permissions / role-based access is tested', priority: 'med' },
      { id: 'c10', text: 'You know your overall code coverage %', priority: 'low' },
      { id: 'c11', text: 'Coverage % is tracked over time', priority: 'low' },
      { id: 'c12', text: 'Coverage gaps are reviewed each sprint', priority: 'low' },
    ],
  },
  {
    id: 'cicd',
    title: 'CI/CD Integration',
    max: 16,
    items: [
      { id: 'ci1', text: 'Automated tests run on every pull request', priority: 'high' },
      { id: 'ci2', text: 'A failing test blocks a merge or deploy', priority: 'high' },
      { id: 'ci3', text: 'Test results are visible directly in the PR / build tool', priority: 'high' },
      { id: 'ci4', text: 'Tests run against a staging environment before production', priority: 'high' },
      { id: 'ci5', text: 'Pipeline failure notifies the relevant dev / team', priority: 'med' },
      { id: 'ci6', text: 'Test run time is under 10 minutes', priority: 'med' },
      { id: 'ci7', text: 'Flaky tests are tracked and addressed promptly', priority: 'med' },
      { id: 'ci8', text: 'Post-deploy smoke tests run in production', priority: 'low' },
    ],
  },
  {
    id: 'framework',
    title: 'Automation Framework',
    max: 16,
    items: [
      { id: 'f1', text: 'Tests use a consistent framework (Playwright, Selenium, Cypress, etc.)', priority: 'high' },
      { id: 'f2', text: 'Test code is version-controlled alongside app code', priority: 'high' },
      { id: 'f3', text: 'Page Object Model (or equivalent abstraction) is used', priority: 'med' },
      { id: 'f4', text: 'Tests are independent — one failure does not cascade', priority: 'med' },
      { id: 'f5', text: 'Test data is managed separately from test logic', priority: 'med' },
      { id: 'f6', text: 'A dev other than the test author can run and maintain the suite', priority: 'med' },
      { id: 'f7', text: 'Tests have meaningful names that explain what they verify', priority: 'low' },
      { id: 'f8', text: 'Framework dependencies are pinned and updated regularly', priority: 'low' },
    ],
  },
  {
    id: 'risk',
    title: 'Risk & Release Readiness',
    max: 14,
    items: [
      { id: 'r1', text: 'You have a documented list of "must-not-break" flows', priority: 'high' },
      { id: 'r2', text: 'A regression suite covers those flows before every release', priority: 'high' },
      { id: 'r3', text: 'You have a rollback plan if a production release fails', priority: 'high' },
      { id: 'r4', text: 'New features require tests before they are marked done', priority: 'med' },
      { id: 'r5', text: 'QA is involved in requirements / planning, not just sign-off', priority: 'med' },
      { id: 'r6', text: 'A release checklist exists and is followed consistently', priority: 'med' },
      { id: 'r7', text: 'Mean time to detect (MTTD) production bugs is tracked', priority: 'low' },
    ],
  },
  {
    id: 'process',
    title: 'Team & Process',
    max: 10,
    items: [
      { id: 'p1', text: 'Developers write tests for their own code (not just a dedicated QA person)', priority: 'high' },
      { id: 'p2', text: 'Bug reports include steps to reproduce and environment details', priority: 'med' },
      { id: 'p3', text: 'Bugs are triaged by severity / priority before assignment', priority: 'med' },
      { id: 'p4', text: 'QA metrics (pass rate, bug escape rate) are reviewed regularly', priority: 'low' },
      { id: 'p5', text: 'The team has a shared definition of done that includes QA criteria', priority: 'low' },
    ],
  },
];

const SCORE_OPTIONS = [
  { value: '0', label: '0', title: 'Not in place' },
  { value: '1', label: '1', title: 'Partially done' },
  { value: '2', label: '2', title: 'Consistently in place' },
  { value: 'na', label: 'N/A', title: 'Not applicable' },
];

const PRIORITY_LABELS = { high: 'High', med: 'Med', low: 'Low' };

function getSectionScore(sectionId, answers) {
  const section = SECTIONS.find(s => s.id === sectionId);
  if (!section) return { score: 0, max: 0 };
  let score = 0, max = 0;
  section.items.forEach(item => {
    const ans = answers[item.id];
    if (ans === 'na' || ans === undefined) return;
    score += parseInt(ans);
    max += 2;
  });
  return { score, max };
}

function getTotalScore(answers) {
  let score = 0, max = 0;
  SECTIONS.forEach(s => {
    const r = getSectionScore(s.id, answers);
    score += r.score;
    max += r.max;
  });
  return { score, max, pct: max > 0 ? Math.round((score / max) * 100) : 0 };
}

function getInterpretation(pct) {
  if (pct >= 80) return { label: 'Strong Foundation', color: 'green', detail: 'Your QA setup is solid. A targeted audit can surface the remaining gaps and sharpen your highest-risk areas.' };
  if (pct >= 60) return { label: 'Good — Gaps to Address', color: 'blue', detail: 'You have coverage but meaningful gaps exist. An audit will identify which ones carry the most risk.' };
  if (pct >= 40) return { label: 'Significant Improvement Needed', color: 'orange', detail: 'Several high-priority areas are missing or inconsistent. An audit will give you a clear, prioritized action plan.' };
  return { label: 'Critical Gaps Present', color: 'red', detail: 'Major QA gaps exist that put releases at risk. An audit is a high-value first step to getting coverage in place fast.' };
}

const AuditSurvey = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ name: '', email: '', company: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const totalSteps = SECTIONS.length + 1;
  const isContactStep = step === SECTIONS.length;
  const isSubmitted = step > SECTIONS.length;

  const currentSection = !isContactStep && !isSubmitted ? SECTIONS[step] : null;

  const sectionAnswered = (section) =>
    section.items.every(item => answers[item.id] !== undefined);

  const canAdvance = isContactStep
    ? contact.name.trim() && contact.email.trim()
    : currentSection && sectionAnswered(currentSection);

  const handleAnswer = (itemId, value) => {
    setAnswers(prev => ({ ...prev, [itemId]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError('');

    const { score, max, pct } = getTotalScore(answers);
    const interpretation = getInterpretation(pct);

    let sectionSummary = '';
    SECTIONS.forEach(section => {
      const { score: ss, max: sm } = getSectionScore(section.id, answers);
      sectionSummary += `${section.title}: ${ss}/${sm}\n`;
      section.items.forEach(item => {
        const ans = answers[item.id];
        const label = ans === 'na' ? 'N/A' : ans === undefined ? '?' : ans;
        sectionSummary += `  [${label}] ${item.text}\n`;
      });
      sectionSummary += '\n';
    });

    const payload = {
      name: contact.name,
      email: contact.email,
      company: contact.company || 'Not provided',
      score: `${pct}% — ${interpretation.label} (${score}/${max} pts)`,
      details: sectionSummary,
      _subject: `QA Assessment Results — ${contact.name}${contact.company ? ' (' + contact.company + ')' : ''} — ${pct}%`,
      _replyto: contact.email,
    };

    try {
      const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setStep(step + 1);
      } else {
        setSubmitError('Something went wrong. Please email mrichard.hudson@mixality.com directly.');
      }
    } catch (err) {
      setSubmitError('Could not send — please email mrichard.hudson@mixality.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  const { pct } = getTotalScore(answers);
  const interpretation = getInterpretation(pct);

  return (
    <div className="audit-page">
      <div className="audit-header-bar">
        <div className="container">
          <div className="audit-progress-row">
            <span className="audit-step-label">
              {isContactStep ? 'Almost done' : isSubmitted ? 'Complete' : `Section ${step + 1} of ${SECTIONS.length}`}
            </span>
            <span className="audit-step-name">
              {isContactStep ? 'Your details' : isSubmitted ? '' : currentSection?.title}
            </span>
          </div>
          <div className="audit-progress-bar">
            <div
              className="audit-progress-fill"
              style={{ width: `${((step) / totalSteps) * 100}%` }}
            />
          </div>
        </div>
      </div>

      <div className="container">
        <AnimatePresence mode="wait">
          {isSubmitted ? (
            <motion.div
              key="submitted"
              className="audit-submitted"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="submitted-icon"><CheckCircle size={48} /></div>
              <h2>Results submitted!</h2>
              <p>Rich will review your assessment and follow up within one business day with personalized recommendations.</p>
              <a href="mailto:mrichard.hudson@mixality.com?subject=Free Discovery Call" className="btn btn-primary">
                Book a free discovery call <ArrowRight size={18} />
              </a>
            </motion.div>
          ) : isContactStep ? (
            <motion.div
              key="contact"
              className="audit-section"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.3 }}
            >
              <div className="audit-score-summary">
                <h2>Your Results</h2>
                <div className={`score-total score-total--${interpretation.color}`}>
                  <span className="score-number">{pct}%</span>
                  <span className="score-label-text">{interpretation.label}</span>
                </div>
                <p className="score-detail">{interpretation.detail}</p>
                <div className="score-breakdown">
                  {SECTIONS.map(s => {
                    const { score: ss, max: sm } = getSectionScore(s.id, answers);
                    const sp = sm > 0 ? Math.round((ss / sm) * 100) : 0;
                    return (
                      <div key={s.id} className="score-row">
                        <span className="score-row-name">{s.title}</span>
                        <div className="score-row-bar">
                          <div className="score-row-fill" style={{ width: `${sp}%` }} />
                        </div>
                        <span className="score-row-pct">{ss}/{sm}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="audit-contact-form">
                <h3>Get a free personal review from Rich</h3>
                <p>Enter your details and Rich will follow up with specific recommendations for your stack — no obligation.</p>
                <div className="contact-fields">
                  <div className="field-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      placeholder="Your name"
                      value={contact.name}
                      onChange={e => setContact(c => ({ ...c, name: e.target.value }))}
                    />
                  </div>
                  <div className="field-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      placeholder="you@company.com"
                      value={contact.email}
                      onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                    />
                  </div>
                  <div className="field-group">
                    <label>Company</label>
                    <input
                      type="text"
                      placeholder="Optional"
                      value={contact.company}
                      onChange={e => setContact(c => ({ ...c, company: e.target.value }))}
                    />
                  </div>
                </div>
                {submitError && <p className="submit-error">{submitError}</p>}
                <div className="audit-nav">
                  <button className="btn btn-secondary" onClick={() => setStep(step - 1)} disabled={submitting}>
                    <ArrowLeft size={18} /> Back
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!canAdvance || submitting}
                    onClick={handleSubmit}
                  >
                    {submitting ? 'Sending…' : 'Submit for Review'} <Send size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key={step}
              className="audit-section"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.3 }}
            >
              <div className="section-intro">
                <span className="section-label">Section {step + 1} of {SECTIONS.length}</span>
                <h2>{currentSection.title}</h2>
                <p className="section-instructions">
                  Rate each item: <strong>0</strong> = not in place · <strong>1</strong> = partially done · <strong>2</strong> = consistently in place · <strong>N/A</strong> = not applicable
                </p>
              </div>

              <div className="audit-items">
                {currentSection.items.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    className="audit-item"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, delay: idx * 0.03 }}
                  >
                    <div className="item-header">
                      <span className={`item-priority priority-${item.priority}`}>
                        {PRIORITY_LABELS[item.priority]}
                      </span>
                      <span className="item-text">{item.text}</span>
                    </div>
                    <div className="item-options">
                      {SCORE_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          className={`score-btn ${answers[item.id] === opt.value ? 'score-btn--selected' : ''} ${opt.value === 'na' ? 'score-btn--na' : ''}`}
                          onClick={() => handleAnswer(item.id, opt.value)}
                          title={opt.title}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="audit-nav">
                {step > 0 && (
                  <button className="btn btn-secondary" onClick={() => setStep(step - 1)}>
                    <ArrowLeft size={18} /> Back
                  </button>
                )}
                <button
                  className={`btn btn-primary ${!canAdvance ? 'btn--disabled' : ''}`}
                  disabled={!canAdvance}
                  onClick={() => canAdvance && setStep(step + 1)}
                >
                  {step === SECTIONS.length - 1 ? 'See My Score' : 'Next Section'} <ArrowRight size={18} />
                </button>
              </div>
              {!canAdvance && (
                <p className="audit-hint">Answer all items to continue — use N/A for anything that doesn't apply.</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default AuditSurvey;
